import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import PageHero from "@/components/PageHero";
import PageLinkCTA from "@/components/PageLinkCTA";
import SuccessCheck from "@/components/SuccessCheck";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/company";
import { postJSON } from "@/lib/api";

const INITIAL = { name: "", company: "", email: "", phone: "", product_requirement: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [params] = useSearchParams();

  // Prefill "Product / Requirement" when arriving from a product enquiry.
  useEffect(() => {
    const p = params.get("product");
    if (p) setForm((f) => ({ ...f, product_requirement: p }));
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await postJSON("/enquiries", form);
      setDone(true);
    } catch {
      setError("Could not send your enquiry right now. Please try again, or email / call us directly.");
    } finally {
      setSending(false);
    }
  };

  const whatsappUrl = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
    "Hello BIK COMMOTRADE (OPC) PVT LTD, I would like to enquire about your railway products."
  )}`;

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's Discuss Your Requirement"
        description="Reach us by email, phone or WhatsApp — or send your enquiry directly through the form below."
      />

      <section className="relative bg-[#F3F7FB] pb-24" data-testid="contact-section">
        <div className="wrap relative grid gap-10 lg:grid-cols-[1fr_1.1fr]">
          {/* Contact cards */}
          <div className="space-y-5">
            <Reveal>
              <div className="card-dark group p-6 transition-all duration-500 hover:border-brand-emeraldLight/40" data-testid="contact-office-card">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-brand-emeraldDark">
                  <MapPin className="h-4 w-4" /> {COMPANY.office.label}
                </p>
                <p className="mt-3 leading-relaxed text-slate-600">{COMPANY.office.lines.join(" ")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="card-dark group p-6 transition-all duration-500 hover:border-brand-emeraldLight/40" data-testid="contact-manufacturing-card">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-brand-emeraldDark">
                  <MapPin className="h-4 w-4" /> {COMPANY.manufacturing.label}
                </p>
                <p className="mt-3 leading-relaxed text-slate-600">{COMPANY.manufacturing.lines.join(" ")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="card-dark p-6" data-testid="contact-email-card">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-brand-emeraldDark">
                  <Mail className="h-4 w-4" /> Email
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {COMPANY.emails.map((e) => (
                    <a
                      key={e}
                      href={`mailto:${e}`}
                      data-testid="contact-email-button"
                      className="btn btn-outline btn-sm !rounded-full"
                    >
                      <Mail className="h-3.5 w-3.5" /> {e}
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="card-dark p-6" data-testid="contact-phone-card">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.26em] text-brand-emeraldDark">
                  <Phone className="h-4 w-4" /> Phone
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {COMPANY.phones.map((p) => (
                    <a
                      key={p.link}
                      href={`tel:${p.link}`}
                      data-testid="contact-call-button"
                      className="btn btn-outline btn-sm !rounded-full"
                    >
                      <Phone className="h-3.5 w-3.5" /> {p.display}
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="btn w-full !bg-[#25D366] !px-7 !py-4 !text-navy-950 hover:!shadow-[0_0_36px_rgba(37,211,102,0.4)]"
              >
                <MessageCircle className="h-5 w-5" />
                Chat on WhatsApp
              </a>
            </Reveal>
          </div>

          {/* Enquiry form */}
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-3xl border border-navy-900/10 bg-white/70 backdrop-blur-md">
              {done ? (
                <div className="p-8 sm:p-12">
                  <SuccessCheck
                    title="Enquiry Received"
                    message="Thank you for reaching out. Our team will review your requirement and respond shortly."
                  >
                    <button
                      type="button"
                      className="btn btn-outline mt-8"
                      onClick={() => {
                        setForm(INITIAL);
                        setDone(false);
                      }}
                      data-testid="contact-send-another"
                    >
                      Send Another Enquiry
                    </button>
                  </SuccessCheck>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="p-6 sm:p-10" data-testid="contact-enquiry-form" noValidate>
                  <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-ink">Send an Enquiry</h2>
                  <p className="mt-2 text-sm text-slate-500">Fields marked * are required.</p>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="field-label" htmlFor="contact-name">Name *</label>
                      <input id="contact-name" className="field" data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your name" required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="contact-company">Company</label>
                      <input id="contact-company" className="field" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company / organisation" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="contact-email">Email *</label>
                      <input id="contact-email" type="email" className="field" data-testid="contact-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="contact-phone">Phone</label>
                      <input id="contact-phone" type="tel" className="field" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 …" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="contact-product">Product / Requirement</label>
                      <input id="contact-product" className="field" data-testid="contact-product-input" value={form.product_requirement} onChange={set("product_requirement")} placeholder="e.g. Earthing Choke" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="contact-message">Message *</label>
                      <textarea id="contact-message" rows={5} className="field resize-none" data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder="Describe your requirement — quantities, application, timeline…" required />
                    </div>
                  </div>

                  {error && (
                    <p className="mt-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="contact-form-error" role="alert">
                      {error}
                    </p>
                  )}

                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="btn btn-primary mt-8 w-full disabled:cursor-wait disabled:opacity-70"
                  >
                    {sending ? "Sending…" : "Send Enquiry"}
                    <Send className="h-4 w-4" />
                  </motion.button>
                </form>
              )}
            </div>
          </Reveal>
        </div>

        {/* Optional map — replace this placeholder with an embedded map later. */}
        <div className="wrap relative mt-10">
          <Reveal>
            <div
              className="relative flex h-56 items-center justify-center overflow-hidden rounded-2xl border border-dashed border-navy-900/15 bg-white/60"
              data-testid="contact-map-placeholder"
            >
              <div className="absolute inset-0 blueprint-grid opacity-60" aria-hidden="true" />
              <div className="relative text-center">
                <MapPin className="mx-auto h-8 w-8 text-brand-emeraldDark" />
                <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.28em] text-slate-500">
                  Map Placeholder
                </p>
                <p className="mt-1 text-xs text-slate-700">
                  An embedded map can be configured here later — office &amp; unit locations above.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="wrap section-pad">
        <PageLinkCTA />
      </section>
    </>
  );
}
